import uniq from 'lodash/uniq';
import uniqBy from 'lodash/uniqBy';
import React, { useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';

import { DataType, GUID, PersonOrg } from '@ndvr/customer-api';
import { WashSaleGroup } from '@ndvr/portfolio-api';

import { themeDefault } from 'styles';
import { getPersonOrgName, useRequiredParams } from 'utils';
import { useClientProfilesForHousehold } from 'views/client/client-queries';
import { useClientPersonOrgs } from 'views/person/person-queries';
import {
    useCreateWashSaleGroup,
    useDeleteWashSaleGroup,
    useUpdateWashSaleGroup,
} from 'views/portfolio/portfolio-queries';
import {
    Button,
    ButtonWithConfirmModal,
    Form,
    FormInput,
    HorizontalLayout,
    IconDelete,
    IconShapesInSquare,
    ModalText,
    PageSpinner,
    Spinner,
    Text,
    Title,
    TwoColumnGrid,
    VerticalLayout,
} from 'widgets';
import { ResourceSearch, ResourceSearchComponentProps } from 'widgets/ResourceSearch';

interface WashSaleGroupFormValues {
    name: string;
    containedPersonOrgs: PersonOrg[];
}
export const CreateEditWashSaleGroup = ({
    onCancel,
    selectedGroup,
}: {
    onCancel: () => void;
    selectedGroup: WashSaleGroup | undefined;
}) => {
    const { globalAccountGUID } = useRequiredParams(['globalAccountGUID']);
    const { mutate: createWashSaleGroup, isPending: isCreateLoading } = useCreateWashSaleGroup(globalAccountGUID);
    const { mutate: updateWashSaleGroup, isPending: isUpdateLoading } = useUpdateWashSaleGroup(globalAccountGUID);
    const { mutate: deleteWashSaleGroup, isPending: isDeleteLoading } = useDeleteWashSaleGroup(globalAccountGUID);
    const { data: personOrgs, isPending: personOrgsLoading } = useClientPersonOrgs();

    const personOrgsMap = useMemo(
        () => new Map(personOrgs?.map((personOrg) => [personOrg.personOrgGUID, personOrg])),
        [personOrgs],
    );

    const onSubmit = (data: WashSaleGroupFormValues) => {
        const washSaleGroupData = {
            name: {
                encryptionContextGUID: globalAccountGUID,
                dataType: DataType.WASH_SALE_GROUP_NAME,
                plainText: data.name,
            },
            containedPersonOrgs: data.containedPersonOrgs.map(({ personOrgGUID }) => personOrgGUID),
        };

        if (selectedGroup) {
            return updateWashSaleGroup(
                {
                    washSaleGroupGUID: selectedGroup.washSaleGroupGUID!,
                    ...washSaleGroupData,
                },
                { onSuccess: () => onCancel() },
            );
        }

        createWashSaleGroup(washSaleGroupData, { onSuccess: () => onCancel() });
    };

    const onDelete = useCallback(() => {
        if (!selectedGroup?.washSaleGroupGUID) {
            return;
        }

        deleteWashSaleGroup(selectedGroup?.washSaleGroupGUID, { onSuccess: () => onCancel() });
    }, [deleteWashSaleGroup, onCancel, selectedGroup?.washSaleGroupGUID]);

    const defaultValues = useMemo(() => {
        const name = selectedGroup?.name?.plainText || '';
        const containedPersonOrgs =
            !!selectedGroup && personOrgsMap
                ? selectedGroup?.containedPersonOrgs.map((guid) => personOrgsMap.get(guid))
                : [];

        return { name, containedPersonOrgs };
    }, [personOrgsMap, selectedGroup]);

    if (personOrgsLoading) {
        return <PageSpinner />;
    }

    return (
        <StyledForm
            defaultValues={defaultValues}
            onSubmit={onSubmit}
            isLoading={isCreateLoading || isUpdateLoading || isDeleteLoading}
        >
            <WashSaleGroupForm
                personOrgsMap={personOrgsMap}
                onCancel={onCancel}
                onDelete={onDelete}
                isEditing={!!selectedGroup}
            />
        </StyledForm>
    );
};

const WashSaleGroupForm = ({
    onCancel,
    onDelete,
    isEditing,
    personOrgsMap,
}: {
    onCancel: () => void;
    onDelete: () => void;
    isEditing: boolean;
    personOrgsMap: Map<GUID<PersonOrg>, PersonOrg>;
}) => {
    const { watch, setValue } = useFormContext();
    const { globalAccountGUID } = useRequiredParams(['globalAccountGUID']);

    const { data: clientProfiles, isPending: clientsLoading } = useClientProfilesForHousehold(globalAccountGUID);
    const { data: personOrgs, isPending: personOrgsLoading } = useClientPersonOrgs();

    const containedPersonOrgs: PersonOrg[] = watch('containedPersonOrgs');
    const name = watch('name');

    const suggestions = useMemo(() => {
        if (!clientProfiles || !personOrgsMap) {
            return [];
        }

        const personOrgGUIDs = uniq(clientProfiles?.map(({ client }) => client.personOrgGUID));
        return personOrgGUIDs.map((guid) => personOrgsMap.get(guid)).filter(Boolean) as PersonOrg[];
    }, [clientProfiles, personOrgsMap]);

    const searchFn = (pattern: RegExp, personOrg: PersonOrg) => {
        const personOrgName = getPersonOrgName(personOrg);
        return [personOrgName, personOrg.personOrgGUID].some((text) => text && pattern.test(text));
    };

    const selectionCheckFn = (personOrg: PersonOrg) => {
        return containedPersonOrgs.some(
            (selectedPersonOrg) => selectedPersonOrg.personOrgGUID === personOrg.personOrgGUID,
        );
    };

    const selectPersonOrg = (personOrg: PersonOrg) => {
        setValue('containedPersonOrgs', uniqBy([...containedPersonOrgs, personOrg], 'personOrgGUID'));
    };

    const removePersonOrg = (personOrg: PersonOrg) => {
        setValue(
            'containedPersonOrgs',
            containedPersonOrgs.filter(({ personOrgGUID }) => personOrg.personOrgGUID !== personOrgGUID),
        );
    };

    return (
        <TwoColumnGrid>
            <Wrapper $gap={48} $overflow="scroll" $padding="32px">
                <Title section>Wash Sale Groups / {isEditing ? 'Edit group' : 'Create new group'}</Title>

                <FormInput
                    vertical
                    label="Group Name"
                    name="name"
                    placeholder="Name Group..."
                    disabled={personOrgsLoading || clientsLoading}
                />

                <VerticalLayout $gap={20}>
                    <HorizontalLayout $rowCenter $gap={8}>
                        <Text largeBody>Selected Account Owners</Text>
                        {isEditing && personOrgsLoading && <Spinner />}
                    </HorizontalLayout>

                    <div>
                        {containedPersonOrgs.length > 0 ? (
                            containedPersonOrgs.map((personOrg: PersonOrg) => (
                                <SelectedListItem key={personOrg.personOrgGUID} $rowCenter $apart>
                                    <ListItem data={personOrg} />
                                    <Button
                                        buttonType="ghost"
                                        size="icon"
                                        onClick={() => removePersonOrg(personOrg)}
                                        icon={<IconDelete size="xSmall" color={themeDefault.text.negative} />}
                                    />
                                </SelectedListItem>
                            ))
                        ) : (
                            <Placeholder $columnCenter $gap={16}>
                                <IconShapesInSquare />
                                <Text largeBody>No Account Owners have been added to this group yet</Text>
                            </Placeholder>
                        )}
                    </div>
                </VerticalLayout>

                <HorizontalLayout $apart>
                    <HorizontalLayout $gap={16}>
                        <Button
                            buttonType="primary"
                            type="submit"
                            disabled={!name || containedPersonOrgs.length < 2 || (isEditing && personOrgsLoading)}
                        >
                            Save Group
                        </Button>
                        <Button buttonType="secondary" onClick={onCancel}>
                            Cancel
                        </Button>
                    </HorizontalLayout>
                    {isEditing && (
                        <ButtonWithConfirmModal
                            buttonProps={{
                                buttonType: 'destructive',
                                children: 'Delete group',
                            }}
                            onConfirm={onDelete}
                            modalTitle={`Delete wash sale group ${name}`}
                        >
                            <ModalText>Are you sure you want to delete this wash sale group?</ModalText>
                        </ButtonWithConfirmModal>
                    )}
                </HorizontalLayout>
            </Wrapper>

            <ResourceSearch
                searchPlaceholder="Search name or GUID..."
                data={personOrgs || []}
                suggestions={suggestions}
                searchFn={searchFn}
                selectionCheckFn={selectionCheckFn}
                Component={ListItem}
                onClick={selectPersonOrg}
                isLoading={personOrgsLoading || clientsLoading}
            />
        </TwoColumnGrid>
    );
};

const StyledForm = styled(Form)`
    height: 100%;
`;

const Wrapper = styled(VerticalLayout)`
    max-width: 700px;
`;

const Placeholder = styled(VerticalLayout)`
    padding: 48px 0;
    color: ${({ theme }) => theme.text.subtle};
    background: ${({ theme }) => theme.container.secondaryBackground};
    border-radius: 10px;
`;

const SelectedListItem = styled(HorizontalLayout)`
    padding: 16px 0;
    border-bottom: 1px solid ${({ theme }) => theme.border.light};

    &:first-child {
        border-top: 1px solid ${({ theme }) => theme.border.light};
    }
`;

const ListItem = ({ data }: ResourceSearchComponentProps<PersonOrg>) => (
    <VerticalLayout>
        <Text largeBody>{getPersonOrgName(data)}</Text>
        <Text helper $themeColor="subtle">
            {data.personOrgType}
        </Text>
    </VerticalLayout>
);
